<script lang="ts">
    import {getLogger} from 'src/core';
    import type {FormControlOptions} from '../../forms/types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {ValueEditorOptions} from '../../../kp-value-editor/types';
    import type {YearValueEditorLocalizations, YearValueEditorValidations} from './types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {getContext, onDestroy} from 'svelte';
    import {FormControlBuilder} from '../../forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {formControls} from '../../forms/use.form-controls';
    import {touched} from '../../shared/use.touched';
    import {style} from 'svelte-forms';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {getDateFormatter, getDateParser} from '../_shared/date-time-utils';
    import {ignoreUnusedProperties} from 'shared/utils/custom-utils';
    import {required} from '../../forms/use.required';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import {
        validatorFactory,
        yearFromNumberToStringFormatter,
        yearFromStringToNumberParser
    } from './utils';

    export let model: number;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: ValueEditorOptions;
    export let validations: YearValueEditorValidations;

    ignoreUnusedProperties(options);

    const localize: EditorsLocalizationFunction<YearValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([(value: string) => value.trim(), getDateParser({
            customViewFormat: 'y',
            customModelFormat: 'y'
        }), yearFromStringToNumberParser()])
        .withFormatters([getDateFormatter({
            customViewFormat: 'y',
            customModelFormat: 'y'
        }), yearFromNumberToStringFormatter()])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    onDestroy(() => {
        subscription.unsubscribe();
    })

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    export function getFormController(): FormControl<number> {
        return formControl;
    }
</script>

<input class="form-control input-{size}"
       type="text"
       id={editorId}
       name={editorName}
       {placeholder}
       disabled={isDisabled}
       use:required={validations.required}
       use:focusOnMount={isFocused}
       use:formControls={formControl}
       use:touched={{formControl}}
       use:style={{field: formControl.getFieldStateAsStore()}}
       use:ariaInvalid={formControl}
       use:ariaErrormessage={formControl}
       use:ariaDescribedby={inputDescription}

       data-main-input/>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>

<Description {inputDescription}/>